import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield } from "lucide-react";

export default function AdminSetup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        email: formData.email,
        password: formData.password,
        name: formData.name
      });
      
      await axios.post(`${API}/admin/setup?${params.toString()}`);
      
      // Log in as admin
      await login(formData.email, formData.password);
      toast.success("Admin account created!");
      navigate("/admin");
    } catch (error) {
      if (error.response?.data?.detail === "Admin already exists") {
        toast.error("Admin already exists. Please log in.");
        navigate("/auth");
      } else {
        toast.error(error.response?.data?.detail || "Failed to create admin");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center" data-testid="admin-setup">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF00FF]/10 flex items-center justify-center">
            <Shield size={32} className="text-[#FF00FF]" />
          </div>
          <span className="text-5xl font-bold text-[#39FF14] neon-text heading">GCSR</span>
          <h1 className="mt-6 text-3xl heading text-[#EDEDED]">Admin Setup</h1>
          <p className="mt-2 text-[#888]">
            Create your admin account to manage your store
          </p>
        </div>

        {/* Form */}
        <div className="bg-[#0A0A0A] border border-[#222] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-[#888] label-text">Your Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-2 gcsr-input w-full"
                placeholder="Store Owner"
                required
                data-testid="admin-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#888] label-text">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="mt-2 gcsr-input w-full"
                placeholder="admin@gcsr.com"
                required
                data-testid="admin-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#888] label-text">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="mt-2 gcsr-input w-full"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="admin-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-skew bg-[#FF00FF] text-white px-8 py-4 font-bold uppercase tracking-wider disabled:opacity-50"
              data-testid="create-admin-btn"
            >
              <span>{loading ? "Creating..." : "Create Admin Account"}</span>
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-[#444] text-sm">
          This will create the first admin account for your store
        </p>
      </div>
    </div>
  );
}
